import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronRight } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import ContactForm from "@/components/landing/ContactForm";
import FAQ from "@/components/landing/FAQ";
import ShowroomDemo from "@/components/landing/ShowroomDemo";
import { SECTORS, SECTOR_LIST } from "@/data/sectors";

export default function SectorPage() {
  const { slug } = useParams();
  const sector = SECTORS[slug];
  const canonical = sector ? `https://sapeur-360.fr/${sector.slug}` : "https://sapeur-360.fr/";
  const ogImage = sector ? `https://sapeur-360.fr${sector.image}` : "";

  // Update document head per page (avoids duplicate description/canonical tags)
  useEffect(() => {
    if (!sector) return;
    const updates = [
      ["__title__", null, sector.title],
      ['meta[name="description"]', "content", sector.metaDescription],
      ['link[rel="canonical"]', "href", canonical],
      ['meta[property="og:title"]', "content", sector.title],
      ['meta[property="og:description"]', "content", sector.metaDescription],
      ['meta[property="og:url"]', "content", canonical],
      ['meta[property="og:image"]', "content", ogImage],
      ['meta[name="twitter:title"]', "content", sector.title],
      ['meta[name="twitter:description"]', "content", sector.metaDescription],
      ['meta[name="twitter:image"]', "content", ogImage],
    ];
    const originals = updates.map(([sel, attr, val]) => {
      if (sel === "__title__") {
        const prev = document.title;
        document.title = val;
        return { titleMode: true, prev };
      }
      const el = document.head.querySelector(sel);
      if (!el) return null;
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, val);
      return { el, attr, prev };
    });
    return () => {
      originals.forEach((o) => {
        if (!o) return;
        if (o.titleMode) { document.title = o.prev; return; }
        if (o.prev === null) o.el.removeAttribute(o.attr);
        else o.el.setAttribute(o.attr, o.prev);
      });
    };
  }, [slug, sector, canonical, ogImage]);

  // FAQ structured data (FAQPage) for rich results
  useEffect(() => {
    if (!sector || !sector.faq) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-faq", "1");
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: sector.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [slug, sector]);

  if (!sector) return <Navigate to="/" replace />;

  const others = SECTOR_LIST.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`sector-page-${sector.slug}`} className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A] antialiased">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-16 lg:pt-36 lg:pb-20">
          <div className="absolute inset-0 blueprint-grid blueprint-grid-fade pointer-events-none" />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-[#52525B] mb-8" aria-label="Fil d'Ariane">
              <Link to="/" className="hover:text-[#0A0A0A] transition-colors">Accueil</Link>
              <ChevronRight className="h-4 w-4 text-[#A1A1AA]" />
              <span className="text-[#0A0A0A] font-semibold">{sector.eyebrow}</span>
            </nav>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 border border-zinc-200 bg-white px-3 py-1.5 rounded-sm">
                  <sector.icon className="h-4 w-4 text-[#E63946]" />
                  <span className="font-mono-tech text-xs uppercase tracking-[0.18em] text-[#52525B]">{sector.eyebrow}</span>
                </span>
                <h1 className="font-display mt-6 text-4xl md:text-5xl font-black tracking-tighter leading-[1.02]">
                  {sector.h1}
                </h1>
                <p className="mt-6 text-lg leading-relaxed text-[#52525B]">{sector.intro}</p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a href="#contact" data-testid="sector-cta"
                    className="group inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#BE123C] text-white font-bold px-7 py-3.5 rounded-sm transition-colors duration-300">
                    Demander une démonstration
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                  <Link to="/#produit"
                    className="inline-flex items-center justify-center gap-2 border border-zinc-300 hover:border-[#0A0A0A] text-[#0A0A0A] font-bold px-7 py-3.5 rounded-sm transition-colors duration-300">
                    Découvrir le produit
                  </Link>
                </div>
              </div>

              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="relative rounded-sm overflow-hidden border border-zinc-200 bg-black aspect-[4/3]">
                <img src={sector.image} alt={sector.imageAlt} className="absolute inset-0 h-full w-full object-cover" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 lg:py-24 bg-white border-y border-zinc-200">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-2xl">
              Pourquoi Sapeur-360 pour {sector.eyebrow.toLowerCase()} ?
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200 rounded-sm overflow-hidden">
              {sector.benefits.map((b, i) => (
                <motion.div key={b.title}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="group bg-white p-8">
                  <span className="grid h-12 w-12 place-items-center bg-[#0A0A0A] text-white rounded-sm group-hover:bg-[#E63946] transition-colors duration-300">
                    <b.icon className="h-6 w-6" strokeWidth={2} />
                  </span>
                  <h3 className="font-display mt-6 text-xl font-bold tracking-tight">{b.title}</h3>
                  <p className="mt-2 text-[#52525B] leading-relaxed">{b.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Text content */}
        <section className="py-20 lg:py-24 bg-[#FAFAFA]">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <div className="space-y-6">
              {sector.paragraphs.map((p, i) => (
                <p key={i} className="text-lg leading-relaxed text-[#3F3F46]">{p}</p>
              ))}
            </div>

            {/* Related sectors — internal linking for SEO */}
            <div className="mt-14 pt-10 border-t border-zinc-200">
              <h3 className="font-mono-tech text-sm uppercase tracking-[0.2em] text-[#A1A1AA] mb-5">Autres secteurs</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {others.map((o) => (
                  <Link key={o.slug} to={`/${o.slug}`} data-testid={`related-${o.slug}`}
                    className="group flex items-center justify-between gap-3 border border-zinc-200 bg-white rounded-sm px-5 py-4 hover:border-[#0A0A0A] transition-colors">
                    <span className="flex items-center gap-3">
                      <o.icon className="h-5 w-5 text-[#E63946]" />
                      <span className="font-bold">{o.eyebrow}</span>
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-[#A1A1AA] group-hover:text-[#E63946] transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {sector.slug === "garages-concessions" && <ShowroomDemo />}

        <FAQ items={sector.faq} />

        <ContactForm />
      </main>

      <Footer />
    </div>
  );
}
